import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ApiError } from '../../lib/api';
import { homeRouteFor, useAuth } from '../../lib/auth';
import { Button, Callout, Field, Input } from '../../components/ui';

export function LoginPage() {
  const { user, status, signIn } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated' && user) {
    return <Navigate to={user.mustChangePassword ? '/change-password' : homeRouteFor(user)} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const signedIn = await signIn(username, password);
      navigate(signedIn.mustChangePassword ? '/change-password' : homeRouteFor(signedIn), {
        replace: true,
      });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not sign in. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-steel-900">
      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-info-600 text-xl font-black text-white">
              OD
            </span>
            <h1 className="mt-4 text-2xl font-bold text-white">ODR</h1>
            <p className="mt-1 text-sm text-steel-400">Operator-Driven Reliability</p>
          </div>

          <form
            onSubmit={submit}
            className="space-y-4 rounded-2xl border border-steel-700 bg-white p-6 shadow-xl"
          >
            <div>
              <h2 className="text-lg font-bold text-steel-900">Sign in</h2>
              <p className="mt-1 text-sm text-steel-500">
                Use the username your supervisor gave you.
              </p>
            </div>

            {error ? <Callout tone="alarm">{error}</Callout> : null}

            <Field label="Username" required>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                enterKeyHint="next"
              />
            </Field>

            <Field label="Password" required>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                enterKeyHint="go"
              />
            </Field>

            <Button type="submit" size="lg" fullWidth loading={busy}>
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-steel-500">
            Having trouble? Ask your shift supervisor or the AM coach to reset your access.
          </p>
        </div>
      </div>
    </div>
  );
}
